import { useState, useEffect } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Box,
  alpha,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import Checkbox from "../Form/Checkbox";
import {
  startGetAdsCampaignMetricsConfiguration,
  startSaveAdsCampaignMetricsConfiguration,
} from "../../actions/adsCampaigns";
import {
  CONFIGURE_METRICS,
  GENERAL_METRIC,
  METRICS_NOT_FOUND_MESSAGE,
  SEARCH_METRICS_PLACEHOLDER,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import {
  GENERAL_METRICS_ADDITIONAL,
  GENERAL_METRICS_ORDERED,
} from "../../pages/NewAdsCampaign/components/Common/platformMetrics";
import { INPUT_COLOR_BORDER } from "../../utils/colors";

/**
 * Reusable component to select metrics visibility.
 * @param {Array<string>} availableMetrics - List of available metrics.
 * @param {Object} metricsVisibility - Visibility state { [metric]: true/false }.
 * @param {Function} setMetricsVisibility - Setter to update visibility.
 * @param {Function} getLabel - Function to get the label of a metric.
 * @param {boolean} isFromStatisticsLevel - Flag to identify if called from StatisticsLevel.
 * @param {string} campaignID - Campaign ID (required if isFromStatisticsLevel is true).
 * @param {string} platform - Platform (required if isFromStatisticsLevel is true).
 * @param {string} levelSectionSelected - Current level selected (platform, adGroup, ad, etc.).
 * @param {Array<string>} generalMetrics - List of general metrics (required if isFromStatisticsLevel is true).
 * @param {Function} onInitializationComplete - Callback that executes when initialization is complete.
 */
const generalMetrics = [
  ...GENERAL_METRICS_ORDERED,
  ...GENERAL_METRICS_ADDITIONAL,
];
const MetricsVisibilitySelector = ({
  availableMetrics = [],
  metricsVisibility = {},
  setMetricsVisibility = () => {},
  getLabel = (m) => m,
  isFromStatisticsLevel = false,
  campaignID = null,
  platform = null,
  levelSectionSelected = null,
  onInitializationComplete = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Redesign: Local state for instant feedback without re-rendering parent immediately
  const [localMetricsVisibility, setLocalMetricsVisibility] =
    useState(metricsVisibility);

  // Sync internal state when external metricsVisibility changes (from outside/load)
  useEffect(() => {
    setLocalMetricsVisibility(metricsVisibility);
  }, [metricsVisibility]);

  useEffect(() => {
    if (isFromStatisticsLevel && campaignID && platform) {
      setIsInitialized(false);
      onLoadMetricsConfiguration();
    }
    // eslint-disable-next-line
  }, [isFromStatisticsLevel, campaignID, platform, levelSectionSelected]);

  useEffect(() => {
    if (
      isFromStatisticsLevel &&
      campaignID &&
      platform &&
      availableMetrics.length > 0 &&
      !isInitialized
    ) {
      onLoadMetricsConfiguration();
    }
    // eslint-disable-next-line
  }, [availableMetrics, levelSectionSelected]);

  const onLoadMetricsConfiguration = async () => {
    if (isInitialized) {
      console.log("Already initialized, skipping...");
      return;
    }

    try {
      setIsLoading(true);

      if (availableMetrics.length === 0) {
        return;
      }

      const configuration = await dispatch(
        startGetAdsCampaignMetricsConfiguration({
          campaignID,
          platform,
        })
      );

      if (configuration) {
        setMetricsVisibility(configuration);
      } else {
        const initialConfiguration = {};
        availableMetrics.forEach((metric) => {
          initialConfiguration[metric] = false;
        });

        generalMetrics.forEach((metric) => {
          if (availableMetrics.includes(metric)) {
            initialConfiguration[metric] = true;
          }
        });

        setMetricsVisibility(initialConfiguration);

        try {
          await dispatch(
            startSaveAdsCampaignMetricsConfiguration({
              campaignID,
              platform,
              metrics: initialConfiguration,
            })
          );
        } catch (error) {
          console.error("Error saving initial metrics configuration:", error);
        }
      }
      setIsInitialized(true);
      onInitializationComplete();
    } catch (error) {
      console.error("Error loading metrics configuration:", error);
      onInitializationComplete();
    } finally {
      setIsLoading(false);
    }
  };

  const onClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const onToggleMetric = (metric) => {
    // 1. Optimistic local update - instant checkbox response
    const newVisibility = {
      ...localMetricsVisibility,
      [metric]: localMetricsVisibility[metric] === false ? true : false,
    };

    setLocalMetricsVisibility(newVisibility);

    // Update parent state synchronously for immediate UI update
    setMetricsVisibility(newVisibility);

    // Save to DB asynchronously
    if (isFromStatisticsLevel && campaignID && platform) {
      dispatch(
        startSaveAdsCampaignMetricsConfiguration({
          campaignID,
          platform,
          metrics: newVisibility,
        })
      )
        .then((success) => {
          if (!success) {
            console.error("Failed to save metrics configuration in DB");
          }
        })
        .catch((error) => {
          console.error("Error saving metrics configuration:", error);
        });
    }
  };

  const filteredMetrics = availableMetrics.filter((metric) => {
    const label = getLabel(metric);
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (isFromStatisticsLevel && !isInitialized && isLoading) {
    return (
      <IconButton
        size="small"
        disabled={true}
        sx={{
          color: "text.secondary",
          width: 32,
          height: 32,
          opacity: 0.5,
        }}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={isLoading}
        sx={{
          color: "text.secondary",
          width: 32,
          height: 32,
          "&:hover": {
            bgcolor: alpha("#3b82f6", 0.1),
            color: "#3b82f6",
          },
          "&:disabled": {
            opacity: 0.5,
          },
        }}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            mt: 1,
            maxHeight: 400,
            width: 300,
          },
        }}
      >
        <MenuItem
          sx={{
            color: "#000",
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {t(CONFIGURE_METRICS)}
        </MenuItem>
        <MenuItem
          sx={{
            borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          }}
        />
        {/* Search field */}
        <Box sx={{ padding: "8px 16px" }}>
          <TextField
            size="small"
            placeholder={t(SEARCH_METRICS_PLACEHOLDER)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.1)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e1e1e1",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#a0aec0",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#a0aec0",
                opacity: 1,
              },
            }}
          />
        </Box>
        <MenuItem
          sx={{
            borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          }}
        />
        {/* Filtered metrics list */}
        {filteredMetrics.map((metric) => {
          const label = getLabel(metric);
          const isGeneralMetric = generalMetrics.includes(metric);
          return (
            <MenuItem
              key={metric}
              onClick={(e) => {
                e.stopPropagation();
                onToggleMetric(metric);
              }}
              sx={{
                "&:hover": { backgroundColor: "#e1e1e1 !important" },
                padding: "8px 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Checkbox
                checked={localMetricsVisibility[metric] !== false}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleMetric(metric)}
              />
              <span
                style={{
                  color: "#000",
                  fontSize: "0.875rem",
                  fontWeight: isGeneralMetric ? "600" : "400",
                }}
              >
                {label}
              </span>
              {isGeneralMetric && (
                <span
                  style={{
                    color: "#a0aec0",
                    fontSize: "0.75rem",
                    marginLeft: "auto",
                  }}
                >
                  {t(GENERAL_METRIC)}
                </span>
              )}
            </MenuItem>
          );
        })}
        {filteredMetrics.length === 0 && (
          <MenuItem
            sx={{
              backgroundColor: "transparent",
              color: "#a0aec0",
              padding: "8px 16px",
            }}
          >
            {t(METRICS_NOT_FOUND_MESSAGE)}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default MetricsVisibilitySelector;
